import React, { useState } from "react";

import QuestionIcon from "@common/assets/images/question-icon.svg";
import Heading from "@common/components/Heading/Heading";
import { Paragraph } from "@common/components/Paragraph/Paragraph.styles";
import { vars } from "@common/styles";
import identifiers from "@common/util/identifiers.json";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";

import { ModuleInfo } from "./moduleInfo";
import * as Styled from "./PageInfoPopover.styles";
import * as StyledPageName from "./PageName.styles";

interface PageInfoPopoverProps {
  info: ModuleInfo;
}

export default function PageInfoPopover({ info }: PageInfoPopoverProps) {
  const [open, setOpen] = useState(false);

  const { x, y, strategy, reference, floating, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    middleware: [offset(10), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });

  // SafePolygon keeps the card open while the pointer travels from the icon into it, so the "Learn more" button stays clickable.
  const hover = useHover(context, {
    delay: { open: 100, close: 120 },
    handleClose: safePolygon({ blockPointerEvents: false }),
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <>
      <StyledPageName.QuestionMark
        ref={reference}
        tabIndex={0}
        data-testid={identifiers["navbar.pageInfo.button"]}
        {...getReferenceProps()}
      >
        <img
          alt={"What's this"}
          src={QuestionIcon}
        />
      </StyledPageName.QuestionMark>

      {open && (
        <FloatingPortal>
          <Styled.Card
            ref={floating}
            data-testid={identifiers["navbar.pageInfo.card"]}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            {...getFloatingProps()}
          >
            <Heading
              heading4
              fontWeight={"bold"}
              marginBottom={12}
            >
              {info.title}
            </Heading>
            <Paragraph
              $p1
              color={vars.colors.grey50}
            >
              {info.description}
            </Paragraph>

            {info.learnMoreUrl && (
              <Styled.LearnMore onClick={() => window.open(info.learnMoreUrl, "_blank")?.focus()}>
                Learn more
                <Styled.ExternalIcon />
              </Styled.LearnMore>
            )}
          </Styled.Card>
        </FloatingPortal>
      )}
    </>
  );
}
